import React from 'react';
import {
    Button,
    List,
    Spinner,
} from 'reactstrap';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { Block, BlockBorder } from '../common/Block';
import {
    AppPageForm, FormLabel, toasty,
} from '../common/forms/FormElements';
import { SaveButton } from '../common/forms/SaveButton';
import { Modal } from '../common/Modal';
import { ExecutionHistory } from './common/ExecutionHistory';

class ManageChargeQuestion extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.formRef = React.createRef();

        this.state = {
            loading: false,
            saving: false,
            isValidated: false,
            chargeTypes: [],
            selectedChargeType: null,
            linkedQuestions: [],
            availableQuestions: [],
            originalQuestionIds: [],
            showAddQuestionModal: false,
            selectedQuestionToAdd: null,
        };
    }

    componentDidMount() {
        Promise.all([
            this.loadChargeTypesForTenant(),
            this.loadAllQuestions(),
        ]);
    }

    loadChargeTypesForTenant = async () => {
        try {
            const raw = await httpClient.js(
                ApiRoutes.chargeType.nonSurcharges(),
            );

            const chargeTypes = (raw || []).map((ct) => ({
                value: ct.id,
                label:
                    ct.description ||
                    ct.baseConfig?.name ||
                    ct.payrollChargeTypeName ||
                    `Charge Type #${ct.id}`,
            }));

            this.setState({ chargeTypes });
        } catch (e) {
            console.error(e);
            toasty.error('Unable to load charge types.');
        }
    };

    loadAllQuestions = async () => {
        try {
            const availableQuestions = await httpClient.js(
                ApiRoutes.typeAheads.getAllQuestion(),
            );
            this.setState({ availableQuestions });
        } catch (e) {
            console.error(e);
            toasty.error('Unable to load questions.');
        }
    };

    onChargeTypeChanged = async (selectedChargeType) => {
        this.setState({
            selectedChargeType,
            linkedQuestions: [],
            originalQuestionIds: [],
            isValidated: false,
            loading: !!selectedChargeType,
        });

        if (!selectedChargeType) return;

        try {
            const linked = await httpClient.js(
                ApiRoutes.typeAheads.chargeTypeQuestion(selectedChargeType.value));

            const linkedQuestions = (linked || []).map((q) => ({
                value: q.id ?? q.value,
                label: q.text ?? q.label,
            }));
            const originalQuestionIds = linkedQuestions.map((q) => q.value);

            this.setState({
                linkedQuestions,
                originalQuestionIds,
                loading: false,
            });
        } catch (e) {
            console.error(e);
            toasty.error('Unable to load questions for charge type.');
            this.setState({ loading: false });
        }
    };

    onRemoveQuestion = (questionId) => {
        this.setState((prev) => ({
            linkedQuestions: prev.linkedQuestions.filter((q) => q.value !== questionId),
        }));
    };

    openAddModal = () => {
        const {
            availableQuestions,
            linkedQuestions,
        } = this.state;

        const linkedIds = new Set(linkedQuestions.map((q) => q.value));
        const candidateOptions = availableQuestions.filter(
            (q) => !linkedIds.has(q.value),
        );


        Modal.show({
            title: 'Add Question',
            onSubmit: (values) => this.confirmAddQuestion(values),
            body: () => (
                <ValidatedSelect
                    id="selectedQuestionToAdd"
                    label="Question"
                    name="selectedQuestionToAdd"
                    options={candidateOptions}
                    placeholder="Select a question"
                    required
                    error="You must select a question to add"
                />
            ),
        });
    };

    confirmAddQuestion = ({ selectedQuestionToAdd }) => {
        const { linkedQuestions, availableQuestions } = this.state;

        if (!selectedQuestionToAdd) {
            toasty.error('Please select a question to add.');
            return;
        }

        const alreadyLinked = linkedQuestions.some((q) => q.value === selectedQuestionToAdd);
        if (alreadyLinked) {
            toasty.error('That question is already linked to this charge type.');
            return;
        }

        this.setState((prev) => ({
            linkedQuestions: [...prev.linkedQuestions, availableQuestions.find((x) => x.value === selectedQuestionToAdd)],
        }));
    };

    onSubmit = async () => {
        const {
            selectedChargeType,
            linkedQuestions,
            originalQuestionIds,
        } = this.state;

        if (!selectedChargeType) {
            this.setState({ isValidated: true });
            toasty.error('Please choose a charge type.');
            return;
        }

        const newSelectedIds = linkedQuestions.map((q) => q.value);

        const addedQuestionIds = newSelectedIds.filter(
            (id) => !originalQuestionIds.includes(id),
        );
        const removedQuestionIds = originalQuestionIds.filter(
            (id) => !newSelectedIds.includes(id),
        );

        this.setState({ saving: true });

        try {
            await httpClient.post(
                ApiRoutes.adminTool.ManageChargeQuestion(selectedChargeType.value),
                {
                    chargeTypeId: selectedChargeType.value,
                    addedQuestionIds,
                    removedQuestionIds,
                },
            );

            toasty.success('Charge type questions saved.');

            await this.onChargeTypeChanged(selectedChargeType);
        } catch (e) {
            console.error(e);
            toasty.error('Could not save questions – see console for details.');
        } finally {
            this.setState({ saving: false });
            if (this.props.refreshUsageHistory) {
                await this.props.refreshUsageHistory();
            }
        }
    };

    renderQuestionsTable() {
        const { linkedQuestions } = this.state;

        if (linkedQuestions.length === 0) {
            return (
                <small className="text-muted">
                    No questions linked – this charge type will not prompt for notes.
                </small>
            );
        }

        return (
            <table className="table table-sm mt-3">
                <thead>
                    <tr>
                        <th>Question</th>
                        <th style={{ width: '1px' }} />
                    </tr>
                </thead>
                <tbody>
                    {linkedQuestions.map((q) => (
                        <tr key={q.value}>
                            <td>{q.label}</td>
                            <td className="text-right">
                                <Button
                                    color="link"
                                    size="sm"
                                    onClick={() => this.onRemoveQuestion(q.value)}
                                >
                                    Remove
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    render() {
        const {
            loading,
            saving,
            isValidated,
            chargeTypes,
            selectedChargeType,
        } = this.state;

        if (!this.context?.user) return null;

        return (
            <AppPageForm
                backUrl={AppNavPaths.AdminTool}
                backLabel="Back to Admin Tools"
                formShown={this.context.formIsOpen}
                formId="manageChargeQuestionsForm"
                formName="manageChargeQuestionsForm"
                formHeading="Manage Charge Type Questions"
                formRef={this.formRef}
                formIsColumn
                saving={saving}
                isValidated={isValidated}
                onSubmit={this.onSubmit}
                setIsValidated={(value) => this.setState({ isValidated: value })}
            >
                <Block padding="medium">
                    <div>
                        <ValidatedSelect
                            id="chargeType"
                            name="chargeType"
                            label="Charge Type"
                            required
                            options={chargeTypes}
                            value={selectedChargeType}
                            onChange={this.onChargeTypeChanged}
                            placeholder="Select a charge type"
                            error="Select a charge type"
                            invalid={isValidated && !selectedChargeType}
                        />

                        {loading && (
                            <div className="d-flex justify-content-center my-2">
                                <Spinner size="sm" />
                                <span className="ml-2 text-muted">Loading questions … </span>
                            </div>
                        )}

                        {selectedChargeType && !loading && (
                            <>
                                <div className="d-flex flex-row flex-nowrap align-items-baseline justify-content-start mt-4">
                                    <FormLabel
                                        className="control-label"
                                        label="Linked Questions"
                                    />

                                    <Button
                                        color="outline-primary"
                                        size="sm"
                                        className="ml-auto"
                                        onClick={this.openAddModal}
                                    >
                                        <i className="fa fa-plus-circle fa-md mr-2" />
                                        Add
                                    </Button>
                                </div>

                                {this.renderQuestionsTable()}
                            </>
                        )}

                        <BlockBorder />

                        <div className="d-flex justify-content-center">
                            <SaveButton
                                saving={saving}
                                label="Submit"
                            />
                        </div>
                    </div>
                </Block>

                <ExecutionHistory usageHistory={this.props.usageHistory}>
                    {(record) => {
                        if (!record?.output) return null;

                        const { output } = record;

                        const chargeTypeName =
                            output.chargeTypeName ??
                            output.chargeType ??
                            output.chargeTypeLabel;

                        const added =
                            output.addedQuestions ??
                            output.AddedQuestions ??
                            [];
                        const removed =
                            output.removedQuestions ??
                            output.RemovedQuestions ??
                            [];

                        return (
                            <List type="unstyled">
                                {chargeTypeName && (
                                    <li>
                                        <strong>Charge Type:</strong>
                                        {' '}
                                        {chargeTypeName}
                                    </li>
                                )}

                                {added.length > 0 && (
                                    <li>
                                        <strong>Added Questions:</strong>
                                        <ul>
                                            {added.map((q, i) => (
                                                <li key={i}>
                                                    {q.label ?? q.text ?? q.name ?? q.id}
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                )}

                                {removed.length > 0 && (
                                    <li>
                                        <strong>Removed Questions:</strong>
                                        <ul>
                                            {removed.map((q, i) => (
                                                <li key={i}>
                                                    {q.label ?? q.text ?? q.name ?? q.id}
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                )}
                            </List>
                        );
                    }}
                </ExecutionHistory>
            </AppPageForm>
        );
    }
}

export default ManageChargeQuestion;
